import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { cleanCoCoName } from "../helpers/cocoNameHelper";
import { useNavigate } from "react-router-dom";
import { FiBriefcase, FiSearch, FiUser, FiX } from "react-icons/fi";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../context/TranslationContext";
import { searchClientsGlobal } from "../helpers/apiHelper";
import {
  GLOBAL_SEARCH_TYPES,
  TYPEAHEAD_DEBOUNCE_MS,
  TYPEAHEAD_GROUPS,
  TYPEAHEAD_MAX_RESULTS,
  buildClientRecordPath,
  buildGlobalSearchUrl,
  buildTypeaheadGroups,
  mapGlobalSearchError,
  validateGlobalSearch,
} from "../helpers/globalSearchHelper";
import SearchHighlight from "./SearchHighlight";
import "../styles/CoordinatorAdvancedSearch.css";

const SLOW_LOADING_MS = 2500;

const GROUP_ICONS = {
  [TYPEAHEAD_GROUPS.CLIENTS]: FiUser,
  [TYPEAHEAD_GROUPS.MATTERS]: FiBriefcase,
};

// Litify-style typeahead over the coordinator global search (ALL mode).
export const CoordinatorAdvancedSearch = () => {
  const { t } = useTranslation();
  const { tokens } = useAuth();
  const navigate = useNavigate();
  const baseId = useId().replace(/:/g, "");
  const listboxId = `${baseId}-listbox`;
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | loading | done | error
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [slow, setSlow] = useState(false);

  const idToken = tokens?.idToken;
  const trimmed = query.trim();
  const invalid = trimmed ? validateGlobalSearch(trimmed, GLOBAL_SEARCH_TYPES.ALL) : "required";

  useEffect(() => {
    setActiveIndex(-1);
    if (invalid || !idToken) {
      setStatus("idle");
      setResponse(null);
      setError(null);
      return undefined;
    }

    const controller = new AbortController();
    const timer = setTimeout(() => {
      setStatus("loading");
      setError(null);
      searchClientsGlobal(trimmed, GLOBAL_SEARCH_TYPES.ALL, idToken, controller.signal, TYPEAHEAD_MAX_RESULTS)
        .then((data) => {
          if (controller.signal.aborted) return;
          setResponse({ ...(data || {}), query: trimmed });
          setStatus("done");
        })
        .catch((requestError) => {
          if (requestError?.name === "AbortError" || controller.signal.aborted) return;
          console.error("Coordinator advanced search failed:", requestError);
          setError(mapGlobalSearchError(requestError));
          setStatus("error");
        });
    }, TYPEAHEAD_DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [idToken, invalid, trimmed]);

  useEffect(() => {
    if (status !== "loading") {
      setSlow(false);
      return undefined;
    }
    const timer = setTimeout(() => setSlow(true), SLOW_LOADING_MS);
    return () => clearTimeout(timer);
  }, [status]);

  useEffect(() => {
    if (!open) return undefined;
    const handlePointerDown = (event) => {
      if (!containerRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [open]);

  const results = useMemo(
    () => (Array.isArray(response?.results) ? response.results : []),
    [response],
  );
  const groups = useMemo(
    () => (status === "done" && !invalid
      ? buildTypeaheadGroups(results, response?.query || trimmed)
      : []),
    [invalid, response?.query, results, status, trimmed],
  );
  const items = useMemo(() => groups.flatMap((group) => group.items), [groups]);
  const totalFound = Number.isFinite(response?.totalFound) ? response.totalFound : results.length;
  const showFooter = !invalid && status === "done" && items.length > 0;
  const optionCount = items.length + (showFooter ? 1 : 0);
  const footerIndex = showFooter ? items.length : -1;
  const showHint = Boolean(trimmed) && Boolean(invalid);
  const expanded = open && (showHint || (!invalid && status !== "idle"));
  const optionId = (index) => `${baseId}-option-${index}`;

  const openFullResults = useCallback(() => {
    if (invalid) return;
    setOpen(false);
    navigate(buildGlobalSearchUrl(trimmed, GLOBAL_SEARCH_TYPES.ALL), {
      state: { query: trimmed },
    });
  }, [invalid, navigate, trimmed]);

  const openItem = (item) => {
    const path = buildClientRecordPath(item?.result);
    if (!path) {
      openFullResults();
      return;
    }
    setOpen(false);
    navigate(path, { state: { activePortal: "coordinator" } });
  };

  const handleKeyDown = (event) => {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setOpen(true);
        if (optionCount) setActiveIndex((index) => (index + 1) % optionCount);
        break;
      case "ArrowUp":
        event.preventDefault();
        setOpen(true);
        if (optionCount) {
          setActiveIndex((index) => (index <= 0 ? optionCount - 1 : index - 1));
        }
        break;
      case "Enter":
        event.preventDefault();
        if (expanded && activeIndex >= 0 && activeIndex < items.length) {
          openItem(items[activeIndex]);
        } else {
          openFullResults();
        }
        break;
      case "Escape":
        if (expanded) {
          event.preventDefault();
          setOpen(false);
          setActiveIndex(-1);
        } else if (query) {
          event.preventDefault();
          setQuery("");
        }
        break;
      case "Tab":
        setOpen(false);
        break;
      default:
    }
  };

  const countLabel = `${totalFound.toLocaleString("pt-BR")}${response?.truncated ? "+" : ""}`;
  let flatIndex = -1;

  return (
    <div className="advanced-search" ref={containerRef}>
      <div className={`advanced-search-field${expanded ? " is-open" : ""}`}>
        <FiSearch className="advanced-search-icon" aria-hidden="true" />
        <label className="sr-only" htmlFor={`${baseId}-input`}>
          {t("advancedSearch.label")}
        </label>
        <input
          ref={inputRef}
          id={`${baseId}-input`}
          type="text"
          className="advanced-search-input"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={expanded}
          aria-controls={listboxId}
          aria-activedescendant={expanded && activeIndex >= 0 ? optionId(activeIndex) : undefined}
          autoComplete="off"
          spellCheck={false}
          placeholder={t("advancedSearch.placeholder")}
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
        />
        {status === "loading" && (
          <span className="advanced-search-spinner" aria-hidden="true" />
        )}
        {query && (
          <button
            type="button"
            className="advanced-search-clear"
            aria-label={t("advancedSearch.clear")}
            onClick={() => {
              setQuery("");
              inputRef.current?.focus();
            }}
          >
            <FiX aria-hidden="true" />
          </button>
        )}
      </div>

      <div
        className="advanced-search-dropdown"
        hidden={!expanded}
        aria-live="polite"
        aria-busy={status === "loading"}
      >
        {showHint && (
          <p className="advanced-search-state">{t("globalSearch.validation.all")}</p>
        )}
        {!showHint && status === "loading" && (
          <p className="advanced-search-state" role="status">
            {t(slow ? "advancedSearch.loadingSlow" : "advancedSearch.loading")}
          </p>
        )}
        {!showHint && status === "error" && (
          <p className="advanced-search-state is-error" role="alert">
            {t(`globalSearch.errors.${error || "GENERIC"}`)}
          </p>
        )}
        {!showHint && status === "done" && items.length === 0 && (
          <p className="advanced-search-state">
            {t(response?.truncated ? "advancedSearch.timedOut" : "advancedSearch.empty")}
          </p>
        )}

        <ul
          id={listboxId}
          role="listbox"
          className="advanced-search-listbox"
          aria-label={t("advancedSearch.label")}
        >
          {!showHint && status === "done" && groups.map(({ group, items: groupItems }) => {
            const Icon = GROUP_ICONS[group];
            return (
              <li key={group} role="presentation" className="advanced-search-group">
                <p className="advanced-search-group-label" id={`${baseId}-group-${group}`}>
                  {t(`advancedSearch.groups.${group}`)}
                </p>
                <ul role="group" aria-labelledby={`${baseId}-group-${group}`}>
                  {groupItems.map((item) => {
                    flatIndex += 1;
                    const index = flatIndex;
                    const { result, matched } = item;
                    return (
                      <li
                        key={item.id}
                        id={optionId(index)}
                        role="option"
                        aria-selected={index === activeIndex}
                        className={`advanced-search-option${index === activeIndex ? " is-active" : ""}`}
                        onMouseDown={(event) => event.preventDefault()}
                        onMouseEnter={() => setActiveIndex(index)}
                        onClick={() => openItem(item)}
                      >
                        <span className="advanced-search-option-icon" aria-hidden="true">
                          <Icon />
                        </span>
                        <span className="advanced-search-option-copy">
                          <span className="advanced-search-option-title">
                            <SearchHighlight text={result?.partyName || "-"} query={response?.query} />
                          </span>
                          <span className="advanced-search-option-meta">
                            {matched.value && (
                              <span className="advanced-search-option-value">
                                <SearchHighlight text={matched.value} query={response?.query} />
                              </span>
                            )}
                            <span className="advanced-search-option-coco">
                              {cleanCoCoName(result?.cocoName) || t("globalSearch.card.noCoCounsel")}
                            </span>
                          </span>
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </li>
            );
          })}
          {showFooter && (
            <li
              id={optionId(footerIndex)}
              role="option"
              aria-selected={activeIndex === footerIndex}
              className={`advanced-search-footer${activeIndex === footerIndex ? " is-active" : ""}`}
              onMouseDown={(event) => event.preventDefault()}
              onMouseEnter={() => setActiveIndex(footerIndex)}
              onClick={openFullResults}
            >
              <FiSearch aria-hidden="true" />
              {t("advancedSearch.viewAll").replace("{count}", countLabel)}
            </li>
          )}
        </ul>
      </div>
    </div>
  );
};

export default CoordinatorAdvancedSearch;
